import fetch from 'unfetch';
import SecureLS from 'secure-ls';

const LEGACY_SSO_STORAGE_KEY = 'ags_sso_staff';
const SSO_SESSION_TOKEN_KEY = 'sis_customer_sso_token';
const SSO_QUERY_KEYS = ['sso_token', 'sso_code'];
const LOGGED_OUT_SSO_TOKEN_KEY = 'sis_customer_logged_out_sso_token';
let cachedCurrentStaff = null;

const clearLegacySsoStorage = () => {
  if (localStorage.getItem(LEGACY_SSO_STORAGE_KEY)) {
    localStorage.removeItem(LEGACY_SSO_STORAGE_KEY);
  }
}

const clearPersistedVuexState = () => {
  const ls = new SecureLS({isCompression: false});
  ls.remove('cms');
}

const clearSsoSessionToken = () => {
  sessionStorage.removeItem(SSO_SESSION_TOKEN_KEY);
}

const clearCurrentStaffCache = (store) => {
  cachedCurrentStaff = null;
  store?.commit('app/setAuthCurrentStaff', null);
}

const getSsoSessionToken = () => {
  return sessionStorage.getItem(SSO_SESSION_TOKEN_KEY);
}

export const hasSsoSessionToken = () => {
  return !!getSsoSessionToken();
}

const setSsoSessionToken = (token) => {
  if (token) sessionStorage.setItem(SSO_SESSION_TOKEN_KEY, token);
}

export const markLoggedOutSsoToken = () => {
  const token = getSsoSessionToken();
  if (token) sessionStorage.setItem(LOGGED_OUT_SSO_TOKEN_KEY, token);
}

const clearLoggedOutSsoToken = () => {
  sessionStorage.removeItem(LOGGED_OUT_SSO_TOKEN_KEY);
}

const isLoggedOutSsoToken = (token) => {
  return !!token && token === sessionStorage.getItem(LOGGED_OUT_SSO_TOKEN_KEY);
}

export const replaceCurrentHistoryWithoutSsoQuery = () => {
  if (!process.client) return;

  const url = new URL(window.location.href);
  const hasSsoQuery = SSO_QUERY_KEYS.some(key => url.searchParams.has(key));
  if (!hasSsoQuery) return;

  SSO_QUERY_KEYS.forEach(key => url.searchParams.delete(key));
  window.history.replaceState(window.history.state, document.title, `${url.pathname}${url.search}${url.hash}`);
}

export const authRedirectUrl = (route) => {
  const path = route.path.replace('/', '');
  return path === 'maisoku' || path.substring(0, path.indexOf('/')) === 'maisoku'
    ? process.env.TRUST_LOGIN_MAISOKU_AUTH_URL
    : process.env.TRUST_LOGIN_AGS_AUTH_URL;
}

const authRequest = async (url, options = {}) => {
  if (!url) return null;

  try {
    const token = getSsoSessionToken();
    const response = await fetch(url, {
      credentials: 'include',
      ...options,
      headers: {
        ...(token ? {Authorization: `Bearer ${token}`} : {}),
        ...(options.body ? {'Content-Type': 'application/json'} : {}),
        ...(options.headers || {}),
      },
    });

    if (!response.ok) return null;
    const text = await response.text();
    return text ? JSON.parse(text) : {};
  } catch (e) {
    return null;
  }
}

const staffMailFromSession = (session) => {
  return session?.mail ?? session?.staff?.mail ?? session?.user?.mail ?? null;
}

const getCachedCurrentStaff = (store) => {
  return store?.getters?.['app/authCurrentStaff'] || cachedCurrentStaff;
}

const setCurrentStaffCache = (staff, store) => {
  cachedCurrentStaff = staff;
  store?.commit('app/setAuthCurrentStaff', staff);
}

export const getCurrentStaff = async ({store, force = false} = {}) => {
  if (!force) {
    const staff = getCachedCurrentStaff(store);
    if (staffMailFromSession(staff)) return staff;
  }

  const staff = await authRequest(process.env.VUE_APP_AUTH_CURRENT_USER_URL);
  if (staffMailFromSession(staff)) {
    setCurrentStaffCache(staff, store);
    clearLoggedOutSsoToken();
    return staff;
  }

  clearCurrentStaffCache(store);
  return null;
}

export const clearAuthenticatedSession = async (store) => {
  clearLegacySsoStorage();
  await authRequest(process.env.VUE_APP_AUTH_LOGOUT_URL, {method: 'POST'});
  clearAuthenticatedCache(store);
}

export const clearAuthenticatedCache = (store) => {
  clearLegacySsoStorage();
  clearPersistedVuexState();
  clearSsoSessionToken();
  clearCurrentStaffCache(store);
  store?.commit('app/resetAuthenticatedState');
}

export default async function ({route, store, app}) {
  const excludeRouteNameList = ['property-property', 'privacy', 'message-line-registration', 'line-tracking'];
  if (excludeRouteNameList.some(routeName => route.name?.includes(routeName))) return;

  clearLegacySsoStorage();

  const hasSsoToken = !!route.query['sso_token'];
  const hasSsoQuery = hasSsoToken || !!route.query['sso_code'];

  // セキュリティ: sso 系クエリは Referer 経由でのトークン漏洩を防ぐため、
  // ネットワークリクエストや外部リダイレクトより前に URL から速やかに除去する。
  // router.replace で履歴エントリを置き換えるため、ブラウザ履歴にもトークンが残らない。
  // （history.replaceState だけだと Vue Router が navigation 完了時に元 URL を再書き込みしてしまうため、
  //   router.replace を使ってルータと URL を一致させる必要がある）
  if (hasSsoQuery) {
    const {sso_token, sso_code, ...queryWithoutSso} = route.query;
    replaceCurrentHistoryWithoutSsoQuery();
    app.router.replace({path: route.path, query: queryWithoutSso, hash: route.hash}).catch(() => {});

    if (isLoggedOutSsoToken(sso_token)) {
      clearAuthenticatedCache(store);
      window.location.replace(authRedirectUrl(route));
      return;
    }

    if (hasSsoToken) {
      clearLoggedOutSsoToken();
      setSsoSessionToken(sso_token);
      clearCurrentStaffCache(store);
    }

    return;
  }

  const staff = await getCurrentStaff({store, force: hasSsoToken});
  if (!staffMailFromSession(staff)) {
    clearAuthenticatedCache(store);
    window.location.replace(authRedirectUrl(route));
    return;
  }
}

export async function getStaffMail(app) {
  const staff = await getCurrentStaff({store: app?.$store});
  return staffMailFromSession(staff);
}
